import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import {
  faDownload,
  faCheck,
  faChevronDown,
  faLayerGroup,
  faPlus,
  faSearch,
  faUsers,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog } from "../components/ui/Dialog";
import {
  downloadModeratorNotesExport,
  fetchModeratorView,
  saveModeratorGroups,
  saveModeratorNotes,
} from "../services/ModeratorViewService";

const BADGE_COLORS = {
  moderator: "#F2994A",
  guest: "#2F80ED",
  new_member: "#27AE60",
  member: "#9CA3AF",
};

const GROUP_PALETTE = [
  "#0F766E",
  "#2563EB",
  "#7C3AED",
  "#BE185D",
  "#D97706",
  "#16A34A",
  "#CA8A04",
  "#334155",
  "#EA580C",
  "#4F46E5",
];

const MODERATOR_NOTE_FIELDS = [
  { key: "businessFocus", label: "Üzleti fókusz", placeholder: "Mire fókuszál üzletileg?" },
  { key: "currentChallenge", label: "Aktuális kihívás", placeholder: "Milyen problémára keres most megoldást?" },
  { key: "opportunityNeed", label: "Lehetőség / igény", placeholder: "Milyen igény, lehetőség vagy ajánlat merült fel?" },
  { key: "personalInfo", label: "Személyes infók", placeholder: "Fontos személyes kontextus, kapcsolódási pont" },
  { key: "tableConnection", label: "Konkrét kapcsolás az asztaltól", placeholder: "Kivel / mivel érdemes összekötni?" },
];

const EMPTY_GUEST_FORM = {
  businessFocus: "",
  currentChallenge: "",
  opportunityNeed: "",
  personalInfo: "",
  tableConnection: "",
};

function normalizeGuestForm(form) {
  return {
    ...EMPTY_GUEST_FORM,
    ...(form ?? {}),
  };
}

function normalizeGroupValue(value) {
  return (value ?? "")
    .toString()
    .trim()
    .replace(/\s+/g, " ");
}

function hashString(value) {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
}

function getGroupColor(groupName) {
  if (!groupName) return "#64748B";
  return GROUP_PALETTE[hashString(groupName.toLowerCase()) % GROUP_PALETTE.length];
}

function getInitials(fullName) {
  return fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function localizeRegistrationState(state) {
  switch (state) {
    case "Registered":
      return "Biztos jön";
    case "Appeared":
      return "Megjelent";
    case "Canceled":
      return "Lemondta";
    case "NotAppeared":
      return "Nem jelent meg";
    case "Waiting":
      return "Várólista";
    case "Interested":
      return "Érdeklődő";
    case "Applied":
      return "Jelentkezett";
    case "Denied":
      return "Elutasított";
    case "WontAttend":
      return "Biztos nem jön";
    default:
      return state ?? "-";
  }
}

function normalizeText(value) {
  return (value ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeGroupId(value, fallbackName) {
  const source = (value ?? fallbackName ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return source || "group";
}

function uniqueNames(values) {
  const unique = new Map();

  values.forEach((value) => {
    const normalized = normalizeGroupValue(value);
    if (!normalized) return;

    const key = normalized.toLowerCase();
    if (!unique.has(key)) {
      unique.set(key, normalized);
    }
  });

  return Array.from(unique.values());
}

function getParticipantGroups(participant) {
  return uniqueNames([...(participant.manualGroups ?? []), ...(participant.autoGroups ?? [])]);
}

function resolveParticipantGroups(participant) {
  return getParticipantGroups(participant);
}

function hasGroup(participant, selectedGroup) {
  if (selectedGroup === "all") return true;
  return getParticipantGroups(participant).some(
    (group) => normalizeText(group) === normalizeText(selectedGroup)
  );
}

function buildDraftGroups(view, rounds) {
  const groupDefinitions = view?.moderatorGroups ?? [];
  const participants = rounds.flatMap((round) => round.participants);

  const drafts = groupDefinitions.map((group, index) => ({
    id: normalizeGroupId(group.id, `${group.name}-${index + 1}`),
    name: group.name,
    color: group.color ?? getGroupColor(group.name),
    memberUserIds: [],
  }));

  const draftsByName = new Map(drafts.map((group) => [normalizeText(group.name), group]));

  participants.forEach((participant) => {
    (participant.manualGroups ?? []).forEach((groupName) => {
      const matchedGroup = draftsByName.get(normalizeText(groupName));
      if (matchedGroup) {
        matchedGroup.memberUserIds.push(Number(participant.userId));
      }
    });
  });

  return drafts.map((group) => ({
    ...group,
    memberUserIds: Array.from(new Set(group.memberUserIds)).sort((a, b) => a - b),
  }));
}

function getSeatAngle(index, total) {
  if (total <= 1) return -90;
  return -90 + (index / total) * 360;
}

function getSeatLayout(index, total) {
  const angle = getSeatAngle(index, total);
  const angleRadians = (angle * Math.PI) / 180;
  const seatRadius = total <= 5 ? 31 : total <= 8 ? 30 : 29;
  const cardX = 50 + Math.cos(angleRadians) * seatRadius;
  const cardY = 50 + Math.sin(angleRadians) * seatRadius;

  return {
    angle,
    cardX,
    cardY,
  };
}

function getRoundKey(round) {
  return `${round.round}-${round.table}`;
}

function reseatParticipants(participants) {
  const sorted = [...participants].sort((left, right) => {
    if (left.seat === 0) return -1;
    if (right.seat === 0) return 1;
    return left.seat - right.seat;
  });
  let nextSeat = 1;

  return sorted.map((participant) => {
    if (participant.seat === 0) {
      return participant;
    }

    const seatedParticipant = {
      ...participant,
      seat: nextSeat,
    };
    nextSeat += 1;

    return seatedParticipant;
  });
}

function Badge({ label, color }) {
  return (
    <span className="moderator-badge" style={{ backgroundColor: color }}>
      {label}
    </span>
  );
}

function GroupFilterPicker({ groups, participantCount, selectedGroup, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const pickerRef = useRef(null);
  const selectedGroupStat = groups.find(
    ({ group }) => normalizeText(group) === normalizeText(selectedGroup)
  );
  const visibleGroups = groups.filter(({ group }) => normalizeText(group).includes(normalizeText(query)));

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const selectGroup = (group) => {
    onSelect(group);
    setOpen(false);
    setQuery("");
  };

  const selectedLabel = selectedGroup === "all" ? "Minden csoport" : selectedGroupStat?.group ?? selectedGroup;
  const selectedCount = selectedGroup === "all" ? participantCount : selectedGroupStat?.count ?? 0;
  const selectedColor = selectedGroup === "all" ? "#2563eb" : getGroupColor(selectedLabel);

  return (
    <div className="moderator-group-picker" ref={pickerRef}>
      <button
        type="button"
        className={`moderator-group-picker-trigger ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="moderator-group-picker-icon">
          <FontAwesomeIcon icon={faUsers} />
        </span>
        <span className="moderator-group-picker-copy">
          <small>Csoportszűrő</small>
          <strong>
            <span className="moderator-group-picker-dot" style={{ backgroundColor: selectedColor }} />
            {selectedLabel}
          </strong>
        </span>
        <span className="moderator-group-picker-count">{selectedCount} fő</span>
        <FontAwesomeIcon className="moderator-group-picker-chevron" icon={faChevronDown} />
      </button>

      {open ? (
        <div className="moderator-group-picker-popover">
          <div className="moderator-group-picker-popover-header">
            <div>
              <strong>Csoport kiválasztása</strong>
              <small>{groups.length} elérhető csoport</small>
            </div>
          </div>
          <label className="moderator-group-picker-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Keresés név vagy szám alapján…"
            />
          </label>
          <div className="moderator-group-picker-options" role="listbox" aria-label="Csoportok">
            {!query ? (
              <button
                type="button"
                role="option"
                aria-selected={selectedGroup === "all"}
                className={`moderator-group-picker-option ${selectedGroup === "all" ? "is-selected" : ""}`}
                onClick={() => selectGroup("all")}
              >
                <span className="moderator-group-picker-option-dot is-all" />
                <span className="moderator-group-picker-option-name">Minden csoport</span>
                <span className="moderator-group-picker-option-count">{participantCount} fő</span>
                {selectedGroup === "all" ? <FontAwesomeIcon icon={faCheck} /> : null}
              </button>
            ) : null}
            {visibleGroups.map(({ group, count }) => {
              const selected = normalizeText(selectedGroup) === normalizeText(group);
              return (
                <button
                  key={group}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`moderator-group-picker-option ${selected ? "is-selected" : ""}`}
                  onClick={() => selectGroup(group)}
                >
                  <span
                    className="moderator-group-picker-option-dot"
                    style={{ backgroundColor: getGroupColor(group) }}
                  />
                  <span className="moderator-group-picker-option-name">{group}</span>
                  <span className="moderator-group-picker-option-count">{count} fő</span>
                  {selected ? <FontAwesomeIcon icon={faCheck} /> : null}
                </button>
              );
            })}
            {!visibleGroups.length ? (
              <div className="moderator-group-picker-empty">Nincs ilyen nevű csoport.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ParticipantDetailDialog({
  participant,
  open,
  onOpenChange,
  onOpenGroupsManager,
  guestForm,
  onGuestFormChange,
  onSaveGuestForm,
  savingGuestForm,
  guestFormStatus,
}) {
  if (!participant) return null;

  const displayedGroups = resolveParticipantGroups(participant);
  const currentGuestForm = normalizeGuestForm(guestForm);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={participant.fullName}
      description={`Kör ${Number(participant.round) + 1} · Asztal ${participant.tableLabel} · ${
        participant.seat === 0 ? "Moderátor" : `Szék ${participant.seat}`
      }`}
      maxWidth="760px"
      footer={
        <div className="moderator-dialog-actions">
          <button
            type="button"
            className="moderator-secondary-button"
            onClick={() => onOpenChange(false)}
          >
            Bezárás
          </button>
          <button
            type="button"
            className="moderator-secondary-button"
            onClick={() => {
              onOpenChange(false);
              onOpenGroupsManager();
            }}
          >
            Csoportok kezelése
          </button>
          <button
            type="button"
            className="moderator-primary-button"
            onClick={onSaveGuestForm}
            disabled={savingGuestForm}
          >
            {savingGuestForm ? "Mentés..." : "Jegyzet mentése"}
          </button>
        </div>
      }
    >
      <div className="moderator-group-editor-card">
        <div className="moderator-dialog-section-title">Résztvevő adatai</div>
        <div className="moderator-participant-detail-grid">
          <div className="moderator-participant-detail-avatar" style={{ backgroundColor: participant.badge?.color ?? BADGE_COLORS.member }}>
            {getInitials(participant.fullName)}
          </div>
          <div className="moderator-participant-detail-copy">
            <div className="moderator-participant-detail-name">{participant.fullName}</div>
            <div className="moderator-participant-picker-meta">
              {participant.companyName ?? "Nincs cég megadva"}
            </div>
            <div className="moderator-participant-picker-meta">{participant.email ?? "-"}</div>
            <div className="moderator-participant-picker-meta">
              {participant.badge ? participant.badge.label : "-"} · {participant.registrationState ? localizeRegistrationState(participant.registrationState) : "-"}
            </div>
          </div>
        </div>

        <div className="moderator-selected-members">
          {displayedGroups.length ? (
            displayedGroups.map((group) => (
              <span key={group} className="moderator-selected-member-chip">
                {group}
              </span>
            ))
          ) : (
            <div className="moderator-empty-inline">Nincs csoport hozzárendelve.</div>
          )}
        </div>
      </div>

      <div className="moderator-group-editor-card">
        <div className="moderator-dialog-section-title">Moderátori jegyzet</div>
        <div className="moderator-guest-note-fields">
          {MODERATOR_NOTE_FIELDS.map((field) => (
            <label key={field.key} className="moderator-form-field">
              <span>{field.label}</span>
              <textarea
                className="moderator-dialog-input moderator-dialog-textarea"
                value={currentGuestForm[field.key]}
                onChange={(event) => onGuestFormChange(field.key, event.target.value)}
                placeholder={field.placeholder}
              />
            </label>
          ))}
        </div>
        {guestFormStatus ? <div className={guestFormStatus.type === "error" ? "moderator-error-box" : "moderator-success-box"}>{guestFormStatus.message}</div> : null}
      </div>
    </Dialog>
  );
}

function ModeratorGroupsDialog({
  open,
  onOpenChange,
  participants,
  initialGroups,
  onSave,
  saving,
}) {
  const [draftGroups, setDraftGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    const nextGroups = (initialGroups ?? []).map((group, index) => ({
      id: normalizeGroupId(group.id, `${group.name}-${index + 1}`),
      name: group.name,
      color: group.color ?? getGroupColor(group.name),
      memberUserIds: Array.from(new Set((group.memberUserIds ?? []).map((memberId) => Number(memberId)))).filter(
        (memberId) => Number.isFinite(memberId) && memberId > 0
      ),
    }));

    setDraftGroups(nextGroups);
    setSelectedGroupId(nextGroups[0]?.id ?? "");
    setNewGroupName("");
    setSearchQuery("");
    setError("");
  }, [initialGroups, open]);

  const selectedGroup = draftGroups.find((group) => group.id === selectedGroupId) ?? draftGroups[0] ?? null;
  const sortedParticipants = useMemo(
    () =>
      [...participants].sort((left, right) => {
        if (left.fullName !== right.fullName) return left.fullName.localeCompare(right.fullName);
        if (left.tableLabel !== right.tableLabel) return left.tableLabel.localeCompare(right.tableLabel);
        return left.seat - right.seat;
      }),
    [participants]
  );

  const filteredParticipants = useMemo(() => {
    const query = normalizeText(searchQuery);
    if (!query) return sortedParticipants;

    return sortedParticipants.filter((participant) => {
      const haystack = [
        participant.fullName,
        participant.companyName,
        participant.email,
        participant.manualGroups?.join(" "),
        participant.autoGroups?.join(" "),
        participant.tableLabel,
      ]
        .filter(Boolean)
        .map(normalizeText)
        .join(" ");

      return haystack.includes(query);
    });
  }, [searchQuery, sortedParticipants]);

  const updateGroup = (groupId, updater) => {
    setDraftGroups((current) =>
      current.map((group) => {
        if (group.id !== groupId) return group;
        return typeof updater === "function" ? updater(group) : { ...group, ...updater };
      })
    );
  };

  const addGroup = () => {
    const name = normalizeGroupValue(newGroupName);
    if (!name) return;

    const nextGroup = {
      id: normalizeGroupId(name, `${name}-${draftGroups.length + 1}`),
      name,
      color: getGroupColor(name),
      memberUserIds: [],
    };

    setDraftGroups((current) => [...current, nextGroup]);
    setSelectedGroupId(nextGroup.id);
    setNewGroupName("");
  };

  const deleteGroup = (groupId) => {
    setDraftGroups((current) => {
      const remaining = current.filter((group) => group.id !== groupId);
      setSelectedGroupId((currentSelected) => {
        if (currentSelected !== groupId) return currentSelected;
        return remaining[0]?.id ?? "";
      });
      return remaining;
    });
  };

  const toggleParticipant = (userId) => {
    if (!selectedGroup) return;

    updateGroup(selectedGroup.id, (group) => {
      const memberSet = new Set(group.memberUserIds);
      if (memberSet.has(userId)) {
        memberSet.delete(userId);
      } else {
        memberSet.add(userId);
      }

      return {
        ...group,
        memberUserIds: Array.from(memberSet).sort((a, b) => a - b),
      };
    });
  };

  const handleSave = async () => {
    setError("");

    if (!draftGroups.length) {
      setError("Legalább egy csoportot hozz létre, mielőtt mented.");
      return;
    }

    const nextGroups = draftGroups.map((group, index) => ({
      id: normalizeGroupId(group.id, `${group.name}-${index + 1}`),
      name: normalizeGroupValue(group.name),
      color: group.color || getGroupColor(group.name),
      memberUserIds: Array.from(new Set(group.memberUserIds.map((memberId) => Number(memberId))))
        .filter((memberId) => Number.isFinite(memberId) && memberId > 0)
        .sort((a, b) => a - b),
    }));

    if (nextGroups.some((group) => !group.name)) {
      setError("Minden csoportnak kell egy név.");
      return;
    }

    const result = await onSave(nextGroups);
    if (!result.ok) {
      setError(result.error);
      return;
    }

    onOpenChange(false);
  };

  const selectedMemberSet = new Set(selectedGroup?.memberUserIds ?? []);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Csoportok kezelése"
      description="Itt tudod létrehozni, színezni, törölni és a résztvevőkhöz rendelni a moderátori csoportokat."
      maxWidth="1320px"
      footer={
        <div className="moderator-dialog-actions">
          <button
            type="button"
            className="moderator-secondary-button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Mégse
          </button>
          <button
            type="button"
            className="moderator-primary-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Mentés..." : "Mentés"}
          </button>
        </div>
      }
    >
      {error ? <div className="moderator-error-box">{error}</div> : null}

      <div className="moderator-group-dialog-layout">
        <aside className="moderator-group-dialog-sidebar">
          <div className="moderator-dialog-section-title">Csoportok</div>
          <div className="moderator-dialog-inline-form moderator-group-create-row">
            <input
              className="moderator-dialog-input"
              type="text"
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addGroup();
                }
              }}
              placeholder="Új csoport neve"
            />
            <button type="button" className="moderator-primary-button" onClick={addGroup}>
              <FontAwesomeIcon icon={faPlus} />
              Hozzáadás
            </button>
          </div>

          <div className="moderator-group-list">
            {draftGroups.length ? (
              draftGroups.map((group) => {
                const isSelected = group.id === selectedGroup?.id;
                return (
                  <div
                    key={group.id}
                    className={`moderator-group-list-item ${isSelected ? "is-selected" : ""}`}
                    style={{ borderColor: group.color }}
                  >
                    <button
                      type="button"
                      className="moderator-group-list-item-main"
                      onClick={() => setSelectedGroupId(group.id)}
                    >
                      <span
                        className="moderator-group-list-item-dot"
                        style={{ backgroundColor: group.color }}
                      />
                      <span className="moderator-group-list-item-copy">
                        <span className="moderator-group-list-item-name">{group.name}</span>
                        <span className="moderator-group-list-item-meta">{group.memberUserIds.length} fő</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="moderator-group-delete-button"
                      aria-label={`Csoport törlése: ${group.name}`}
                      onClick={() => deleteGroup(group.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="moderator-empty-inline">Még nincs létrehozott csoport.</div>
            )}
          </div>
        </aside>

        <section className="moderator-group-dialog-editor">
          {selectedGroup ? (
            <>
              <div className="moderator-group-editor-card">
                <div className="moderator-dialog-section-title">Csoport adatai</div>
                <div className="moderator-group-editor-grid">
                  <label className="moderator-form-field">
                    <span>Név</span>
                    <input
                      className="moderator-dialog-input"
                      type="text"
                      value={selectedGroup.name}
                      onChange={(event) =>
                        updateGroup(selectedGroup.id, { name: event.target.value })
                      }
                      placeholder="Például: VIP"
                    />
                  </label>
                  <label className="moderator-form-field">
                    <span>Szín</span>
                    <div className="moderator-color-row">
                      <input
                        className="moderator-color-input"
                        type="color"
                        value={selectedGroup.color}
                        onChange={(event) =>
                          updateGroup(selectedGroup.id, { color: event.target.value })
                        }
                      />
                      <div className="moderator-color-palette">
                        {GROUP_PALETTE.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`moderator-color-swatch ${selectedGroup.color === color ? "is-selected" : ""}`}
                            style={{ backgroundColor: color }}
                            onClick={() => updateGroup(selectedGroup.id, { color })}
                            aria-label={`Szín választása: ${color}`}
                          />
                        ))}
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="moderator-group-editor-card">
                <div className="moderator-group-editor-header">
                  <div>
                    <div className="moderator-dialog-section-title">Résztvevők hozzárendelése</div>
                    <div className="moderator-group-editor-subtitle">
                      Kereséssel gyorsan tudsz embereket hozzáadni vagy kivenni a csoportból.
                    </div>
                  </div>
                  <label className="moderator-search-box">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Keresés név, email, asztal alapján"
                    />
                  </label>
                </div>

                <div className="moderator-selected-members">
                  {(selectedGroup.memberUserIds ?? []).length ? (
                    [...selectedGroup.memberUserIds]
                      .map((memberUserId) => participants.find((participant) => Number(participant.userId) === memberUserId))
                      .filter(Boolean)
                      .slice(0, 10)
                      .map((participant) => (
                        <span key={participant.userId} className="moderator-selected-member-chip">
                          {participant.fullName}
                        </span>
                      ))
                  ) : (
                    <div className="moderator-empty-inline">Még nincs hozzárendelve résztvevő.</div>
                  )}
                </div>

                <div className="moderator-participant-picker">
                  {filteredParticipants.map((participant) => {
                    const isSelected = selectedMemberSet.has(Number(participant.userId));

                    return (
                      <button
                        key={participant.userId}
                        type="button"
                        className={`moderator-participant-picker-row ${isSelected ? "is-selected" : ""}`}
                        onClick={() => toggleParticipant(Number(participant.userId))}
                      >
                        <div className="moderator-participant-picker-leading">
                          <div
                            className="moderator-participant-picker-avatar"
                            style={{ backgroundColor: participant.badge?.color ?? BADGE_COLORS.member }}
                          >
                            {getInitials(participant.fullName)}
                          </div>
                          <div className="moderator-participant-picker-info">
                            <div className="moderator-participant-picker-name">{participant.fullName}</div>
                            <div className="moderator-participant-picker-meta">
                              {participant.companyName ?? "Nincs cég megadva"} · Asztal {participant.tableLabel}
                              {participant.seat === 0 ? " · Moderátor" : ` · Szék ${participant.seat}`}
                            </div>
                            <div className="moderator-participant-picker-meta">
                              {participant.email ?? "-"}
                            </div>
                          </div>
                        </div>

                        <div className="moderator-participant-picker-badges">
                          {participant.badge ? <Badge label={participant.badge.label} color={participant.badge.color} /> : null}
                          {participant.registrationState ? (
                            <Badge
                              label={localizeRegistrationState(participant.registrationState)}
                              color="#64748B"
                            />
                          ) : null}
                          <span className="moderator-participant-picker-toggle">
                            {isSelected ? "Eltávolítás" : "Hozzáadás"}
                          </span>
                        </div>
                      </button>
                    );
                  })}

                  {!filteredParticipants.length ? (
                    <div className="moderator-empty-inline">Nincs találat ehhez a kereséshez.</div>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <div className="moderator-empty-inline">Hozz létre vagy válassz ki egy csoportot.</div>
          )}
        </section>
      </div>
    </Dialog>
  );
}

function RoundSeatCard({
  participant,
  layout,
  selectedGroup,
  onOpenParticipant,
  activeParticipantId,
  hoveredParticipantId,
  onSelectParticipant,
  onHoverParticipant,
  draggedParticipantId,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) {
  const isHighlighted = hasGroup(participant, selectedGroup);
  const isActive = Number(activeParticipantId) === Number(participant.userId);
  const isHovered = Number(hoveredParticipantId) === Number(participant.userId);
  const isModerator = participant.seat === 0;
  const accentColor = participant.badge?.color ?? BADGE_COLORS.member;
  const isDragged = Number(draggedParticipantId) === Number(participant.userId);

  return (
    <div
      className={`moderator-seat-anchor ${isHighlighted ? "" : "is-dimmed"} ${isActive ? "is-active" : ""} ${isHovered ? "is-hovered" : ""} ${isDragged ? "is-dragging" : ""}`}
      style={{
        left: `${layout.cardX}%`,
        top: `${layout.cardY}%`,
        "--seat-accent": accentColor,
        "--seat-angle": `${layout.angle + 90}deg`,
        "--seat-text-angle": `${-(layout.angle + 90)}deg`,
      }}
    >
      <button
        type="button"
        className={`moderator-seat-card ${isModerator ? "is-moderator" : ""}`}
        draggable
        onDragStart={(event) => onDragStart(event, participant)}
        onDragOver={onDragOver}
        onDrop={(event) => onDrop(event, participant)}
        onDragEnd={onDragEnd}
        onClick={() => {
          onSelectParticipant(participant);
          onOpenParticipant(participant);
        }}
        onMouseEnter={() => onHoverParticipant(participant.userId)}
        onMouseLeave={() => onHoverParticipant(null)}
        title={`${isModerator ? "Moderátor" : `Szék ${participant.seat}`} · ${participant.fullName}`}
      >
        <span className="moderator-seat-card-ring" />
        <span className="moderator-seat-card-number">{isModerator ? "M" : participant.seat}</span>
      </button>
    </div>
  );
}

function RoundParticipantList({
  participants,
  selectedGroup,
  activeParticipantId,
  hoveredParticipantId,
  onOpenParticipant,
  onSelectParticipant,
  onHoverParticipant,
  draggedParticipantId,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) {
  return (
    <div className="moderator-seat-rail">
      <div className="moderator-seat-rail-header">
        <div className="moderator-seat-rail-title">Résztvevők</div>
        <div className="moderator-seat-rail-subtitle">Kattints egy kártyára a részletekhez</div>
      </div>

      <div className="moderator-seat-rail-list">
        {participants.map((participant) => {
          const isHighlighted = hasGroup(participant, selectedGroup);
          const isActive = Number(activeParticipantId) === Number(participant.userId);
          const isHovered = Number(hoveredParticipantId) === Number(participant.userId);
          const isModerator = participant.seat === 0;
          const accentColor = participant.badge?.color ?? BADGE_COLORS.member;
          const groups = getParticipantGroups(participant);
          const isDragged = Number(draggedParticipantId) === Number(participant.userId);

          return (
            <button
              key={participant.userId}
              type="button"
              className={`moderator-seat-rail-card ${isActive ? "is-active" : ""} ${isHovered ? "is-hovered" : ""} ${isHighlighted ? "" : "is-dimmed"} ${isDragged ? "is-dragging" : ""}`}
              style={{ "--seat-accent": accentColor }}
              draggable
              onDragStart={(event) => onDragStart(event, participant)}
              onDragOver={onDragOver}
              onDrop={(event) => onDrop(event, participant)}
              onDragEnd={onDragEnd}
              onClick={() => {
                onSelectParticipant(participant);
                onOpenParticipant(participant);
              }}
              onMouseEnter={() => onHoverParticipant(participant.userId)}
              onMouseLeave={() => onHoverParticipant(null)}
            >
              <span
                className={`moderator-seat-rail-seat ${isModerator ? "is-moderator" : ""}`}
                style={{ backgroundColor: accentColor }}
              >
                {isModerator ? "M" : participant.seat}
              </span>

              <span className="moderator-seat-rail-content">
                <span className="moderator-seat-rail-name">{participant.fullName}</span>
                <span className="moderator-seat-rail-meta">
                  {participant.companyName || groups[0] || "Résztvevő"}
                </span>
              </span>

              {groups.length ? (
                <span
                  className="moderator-seat-rail-group"
                  style={{
                    backgroundColor: `${getGroupColor(groups[0])}18`,
                    color: getGroupColor(groups[0]),
                  }}
                >
                  {groups[0]}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RoundTableCard({
  round,
  selectedGroup,
  onOpenParticipant,
  activeParticipantId,
  hoveredParticipantId,
  onSelectParticipant,
  onHoverParticipant,
  onSwapParticipants,
  tableMeta,
  onTableMetaChange,
}) {
  const sortedParticipants = [...round.participants].sort((a, b) => {
    if (a.seat === 0) return -1;
    if (b.seat === 0) return 1;
    return a.seat - b.seat;
  });
  const seatLayouts = sortedParticipants.map((_, index) => getSeatLayout(index, sortedParticipants.length));
  const [draggedParticipantId, setDraggedParticipantId] = useState(null);
  const seatCardSize = sortedParticipants.length > 16 ? "48px" : sortedParticipants.length > 10 ? "56px" : undefined;
  const openRoundParticipant = (participant) => {
    onOpenParticipant({
      ...participant,
      round: round.round,
      table: round.table,
      tableLabel: round.tableLabel,
    });
  };

  const handleDragStart = (event, participant) => {
    setDraggedParticipantId(participant.userId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", participant.userId.toString());
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event, targetParticipant) => {
    event.preventDefault();
    const sourceUserId = Number(event.dataTransfer.getData("text/plain"));
    setDraggedParticipantId(null);

    if (!sourceUserId || sourceUserId === Number(targetParticipant.userId)) return;
    onSwapParticipants(round, sourceUserId, Number(targetParticipant.userId));
  };

  const handleDragEnd = () => {
    setDraggedParticipantId(null);
  };

  return (
    <div className="moderator-round-card">
      <div className="moderator-round-card-top">
        <div>
          <div className="moderator-round-card-title">
            Kör {Number(round.round) + 1} · Asztal {round.tableLabel}
          </div>
          <div className="moderator-round-card-subtitle">
            {round.participants.length} fő · {round.moderator ? `Moderátor: ${round.moderator.fullName}` : "Nincs moderátor"}
          </div>
        </div>

        {round.moderator ? (
          <Badge label={`Moderátor: ${round.moderator.fullName}`} color={BADGE_COLORS.moderator} />
        ) : null}
      </div>

      <div className="moderator-round-layout">
        <div className="moderator-seating-panel">

          <div className="moderator-seating-panel-header">
            <div>
              <div className="moderator-seating-panel-kicker">Asztalnézet</div>
              <div className="moderator-seating-panel-title">Körkiosztás</div>
            </div>
            <div className="moderator-seating-panel-hint">Húzd rá az egyik széket a másikra a sorrendcseréhez</div>
          </div>

          <div className="moderator-seating-diagram" style={seatCardSize ? { "--seat-card-size": seatCardSize } : undefined}>
            <div className="moderator-table-circle" />
            <div className="moderator-table-center-card">
              <div className="moderator-table-center-label">Asztal</div>
              <div className="moderator-table-center-value">{round.tableLabel}</div>
              <div className="moderator-table-center-round">Kör {Number(round.round) + 1}</div>
            </div>

            {sortedParticipants.map((participant, index) => (
              <RoundSeatCard
                key={participant.userId}
                participant={participant}
                layout={seatLayouts[index]}
                selectedGroup={selectedGroup}
                onOpenParticipant={openRoundParticipant}
                activeParticipantId={activeParticipantId}
                hoveredParticipantId={hoveredParticipantId}
                onSelectParticipant={onSelectParticipant}
                onHoverParticipant={onHoverParticipant}
                draggedParticipantId={draggedParticipantId}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>

          <div className="moderator-table-meta-card">
            <div className="moderator-dialog-section-title">Asztal megjegyzés és időpont</div>
            <div className="moderator-table-meta-grid">
              <label className="moderator-form-field">
                <span>Időpont</span>
                <input
                  className="moderator-dialog-input"
                  type="time"
                  value={tableMeta.time}
                  onChange={(event) => onTableMetaChange(round, "time", event.target.value)}
                />
              </label>
              <label className="moderator-form-field">
                <span>Megjegyzés</span>
                <textarea
                  className="moderator-dialog-input moderator-dialog-textarea"
                  value={tableMeta.note}
                  onChange={(event) => onTableMetaChange(round, "note", event.target.value)}
                  placeholder="Belső megjegyzés az asztalhoz"
                />
              </label>
            </div>
            <div className="moderator-empty-inline">Frontend-only előkészítés, egyelőre nem ment API-ra.</div>
          </div>

        </div>

        <RoundParticipantList
          participants={sortedParticipants}
          selectedGroup={selectedGroup}
          activeParticipantId={activeParticipantId}
          hoveredParticipantId={hoveredParticipantId}
          onOpenParticipant={openRoundParticipant}
          onSelectParticipant={onSelectParticipant}
          onHoverParticipant={onHoverParticipant}
          draggedParticipantId={draggedParticipantId}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        />
      </div>
    </div>
  );
}

function getVisibleHeading(selectedGroup) {
  if (selectedGroup === "all") {
    return "Ültetési adminisztráció";
  }

  return `Szűrés: ${selectedGroup}`;
}

export function ModeratorView() {
  const { event } = useOutletContext();
  const [view, setView] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [activeRoundKey, setActiveRoundKey] = useState("");
  const [activeParticipantId, setActiveParticipantId] = useState(null);
  const [hoveredParticipantId, setHoveredParticipantId] = useState(null);
  const [activeParticipant, setActiveParticipant] = useState(null);
  const [guestForms, setGuestForms] = useState({});
  const [guestFormStatus, setGuestFormStatus] = useState(null);
  const [savingGuestForm, setSavingGuestForm] = useState(false);
  const [tableMeta, setTableMeta] = useState({});
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);
  const [exportingNotes, setExportingNotes] = useState(false);

  const loadView = async () => {
    setIsLoading(true);
    setError("");

    const result = await fetchModeratorView();
    if (!result.ok) {
      setError(result.error);
      setView(null);
      setIsLoading(false);
      return;
    }

    setView(result.data);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!event?.id) return;
    loadView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const rounds = useMemo(() => view?.moderatedRounds ?? [], [view]);
  const activeRound = useMemo(() => {
    if (!rounds.length) return null;
    return rounds.find((round) => getRoundKey(round) === activeRoundKey) ?? rounds[0];
  }, [activeRoundKey, rounds]);
  const allParticipants = useMemo(() => rounds.flatMap((round) => round.participants), [rounds]);
  const groupStats = useMemo(() => {
    const stats = new Map();

    allParticipants.forEach((participant) => {
      uniqueNames(getParticipantGroups(participant)).forEach((group) => {
        const current = stats.get(group) ?? { group, count: 0 };
        current.count += 1;
        stats.set(group, current);
      });
    });

    return Array.from(stats.values()).sort(
      (a, b) => b.count - a.count || a.group.localeCompare(b.group, "hu", { numeric: true })
    );
  }, [allParticipants]);
  const selectedGroupCount = useMemo(
    () => groupStats.find(({ group }) => normalizeText(group) === normalizeText(selectedGroup))?.count ?? 0,
    [groupStats, selectedGroup]
  );

  const draftGroups = useMemo(() => buildDraftGroups(view, rounds), [view, rounds]);

  useEffect(() => {
    if (!rounds.length) {
      setActiveRoundKey("");
      return;
    }

    if (!activeRoundKey || !rounds.some((round) => getRoundKey(round) === activeRoundKey)) {
      setActiveRoundKey(getRoundKey(rounds[0]));
    }
  }, [activeRoundKey, rounds]);

  useEffect(() => {
    if (selectedGroup === "all") return;

    const selectedGroupStillExists = groupStats.some(
      ({ group }) => normalizeText(group) === normalizeText(selectedGroup)
    );

    if (!selectedGroupStillExists) {
      setSelectedGroup("all");
    }
  }, [groupStats, selectedGroup]);

  const openParticipant = (participant) => {
    setActiveParticipantId(participant.userId);
    setActiveParticipant(participant);
    setGuestFormStatus(null);
  };

  const closeParticipantDialog = () => {
    setActiveParticipant(null);
  };

  const handleSaveGroups = async (groups) => {
    setSavingGroups(true);
    const result = await saveModeratorGroups(groups);
    if (!result.ok) {
      setSavingGroups(false);
      return result;
    }

    await loadView();
    setSavingGroups(false);
    return result;
  };

  const handleSwapParticipants = (round, sourceUserId, targetUserId) => {
    setView((currentView) => {
      if (!currentView) return currentView;

      const currentRoundKey = getRoundKey(round);

      return {
        ...currentView,
        moderatedRounds: currentView.moderatedRounds.map((currentRound) => {
          if (getRoundKey(currentRound) !== currentRoundKey) return currentRound;

          const sourceParticipant = currentRound.participants.find(
            (participant) => Number(participant.userId) === Number(sourceUserId)
          );
          const targetParticipant = currentRound.participants.find(
            (participant) => Number(participant.userId) === Number(targetUserId)
          );

          if (!sourceParticipant || !targetParticipant) return currentRound;

          const sourceSeat = sourceParticipant.seat;
          const targetSeat = targetParticipant.seat;

          return {
            ...currentRound,
            participants: reseatParticipants(
              currentRound.participants.map((participant) => {
                if (Number(participant.userId) === Number(sourceUserId)) {
                  return { ...participant, seat: targetSeat };
                }

                if (Number(participant.userId) === Number(targetUserId)) {
                  return { ...participant, seat: sourceSeat };
                }

                return participant;
              })
            ),
          };
        }),
      };
    });
  };

  const handleTableMetaChange = (round, field, value) => {
    const key = getRoundKey(round);

    setTableMeta((current) => ({
      ...current,
      [key]: {
        time: current[key]?.time ?? "",
        note: current[key]?.note ?? "",
        [field]: value,
      },
    }));
  };

  const handleExportModeratorNotes = async () => {
    setExportingNotes(true);
    setError("");

    const result = await downloadModeratorNotesExport();
    if (!result.ok) {
      setError(result.error);
      setExportingNotes(false);
      return;
    }

    const url = window.URL.createObjectURL(result.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = result.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
    setExportingNotes(false);
  };

  const handleGuestFormChange = (field, value) => {
    if (!activeParticipant) return;

    const key = activeParticipant.userId.toString();
    setGuestFormStatus(null);
    setGuestForms((current) => ({
      ...current,
      [key]: {
        ...normalizeGuestForm(activeParticipant.moderatorNotes),
        ...normalizeGuestForm(current[key]),
        [field]: value,
      },
    }));
  };

  const handleSaveGuestForm = async () => {
    if (!activeParticipant) return;

    const key = activeParticipant.userId.toString();
    const currentForm = normalizeGuestForm(guestForms[key] ?? activeParticipant.moderatorNotes);
    const moderatorNotes = MODERATOR_NOTE_FIELDS.reduce((notes, field) => {
      notes[field.key] = currentForm[field.key];
      return notes;
    }, {});

    setSavingGuestForm(true);
    setGuestFormStatus(null);

    const result = await saveModeratorNotes(activeParticipant.userId, moderatorNotes);
    if (!result.ok) {
      setSavingGuestForm(false);
      setGuestFormStatus({ type: "error", message: result.error });
      return;
    }

    const savedNotes = normalizeGuestForm(result.data?.moderatorNotes ?? moderatorNotes);
    setGuestForms((current) => ({
      ...current,
      [key]: savedNotes,
    }));
    setActiveParticipant((current) => current ? { ...current, moderatorNotes: savedNotes } : current);
    setView((currentView) => {
      if (!currentView) return currentView;
      return {
        ...currentView,
        moderatedRounds: currentView.moderatedRounds.map((round) => ({
          ...round,
          participants: round.participants.map((participant) =>
            Number(participant.userId) === Number(activeParticipant.userId)
              ? { ...participant, moderatorNotes: savedNotes }
              : participant
          ),
        })),
      };
    });
    setSavingGuestForm(false);
    setGuestFormStatus({ type: "success", message: "Moderátori jegyzet mentve." });
  };

  if (isLoading && !view) {
    return (
      <div
        className="spinner-border"
        role="status"
        style={{
          "--bs-spinner-width": "5rem",
          "--bs-spinner-height": "5rem",
          "--bs-spinner-border-width": "0.5em",
        }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
    );
  }

  return (
    <div className="row wrapper moderator-view-page">
      <div className="col-12">
        <div className="moderator-hero-card">
          <div className="moderator-hero-copy">
            <div className="moderator-hero-kicker">{getVisibleHeading(selectedGroup)}</div>
            <h2 className="moderator-hero-title">Moderátori asztalnézet</h2>
            <p className="moderator-hero-description">
              Az eseményhez kapcsolódó ültetési és csoport adminiszráció
            </p>
          </div>

          <div className="moderator-hero-actions">
            <div className="moderator-hero-stats">
              <div className="moderator-stat-card">
                <div className="moderator-stat-value">{rounds.length}</div>
                <div className="moderator-stat-label">Asztal</div>
              </div>
              <div className="moderator-stat-card">
                <div className="moderator-stat-value">{allParticipants.length}</div>
                <div className="moderator-stat-label">Résztvevő</div>
              </div>
              <div className="moderator-stat-card">
                <div className="moderator-stat-value">{groupStats.length}</div>
                <div className="moderator-stat-label">Csoport</div>
              </div>
            </div>

            <button
              type="button"
              className="moderator-primary-button moderator-hero-cta"
              onClick={() => setGroupsDialogOpen(true)}
            >
              <FontAwesomeIcon icon={faLayerGroup} />
              Csoportok kezelése
            </button>
            <button
              type="button"
              className="moderator-secondary-button moderator-hero-cta"
              onClick={handleExportModeratorNotes}
              disabled={exportingNotes || !rounds.length}
            >
              <FontAwesomeIcon icon={faDownload} />
              {exportingNotes ? "Exportálás..." : "Jegyzet export"}
            </button>
          </div>
        </div>

        {error ? <div className="moderator-error-box">{error}</div> : null}

        {groupStats.length ? (
          <div className="moderator-toolbar">
            <GroupFilterPicker
              groups={groupStats}
              participantCount={allParticipants.length}
              selectedGroup={selectedGroup}
              onSelect={setSelectedGroup}
            />
            <div className="moderator-toolbar-hint">
              {selectedGroup === "all"
                ? "Válassz csoportot az asztalok gyors szűréséhez."
                : `${selectedGroupCount} résztvevő látható a kiválasztott csoportban.`}
            </div>
          </div>
        ) : null}

        {!rounds.length ? (
          <div className="moderator-empty-state">
            Nincs még moderátori ültetés ehhez az eseményhez.
          </div>
        ) : (
          <>
            <div className="moderator-table-tabs" role="tablist" aria-label="Asztalok">
              {rounds.map((round) => {
                const roundKey = getRoundKey(round);
                const isActive = roundKey === getRoundKey(activeRound);

                return (
                  <button
                    key={roundKey}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`moderator-table-tab ${isActive ? "is-active" : ""}`}
                    onClick={() => setActiveRoundKey(roundKey)}
                  >
                    <span>{round.tableLabel} asztal</span>
                    <small>{Number(round.round) + 1}. kör · {round.participants.length} fő</small>
                  </button>
                );
              })}
            </div>

            {activeRound ? (
              <RoundTableCard
                key={getRoundKey(activeRound)}
                round={activeRound}
                selectedGroup={selectedGroup}
                onOpenParticipant={openParticipant}
                activeParticipantId={activeParticipantId}
                hoveredParticipantId={hoveredParticipantId}
                onSelectParticipant={(participant) => setActiveParticipantId(participant.userId)}
                onHoverParticipant={setHoveredParticipantId}
                onSwapParticipants={handleSwapParticipants}
                tableMeta={tableMeta[getRoundKey(activeRound)] ?? { time: "", note: "" }}
                onTableMetaChange={handleTableMetaChange}
              />
            ) : null}
          </>
        )}

        <ModeratorGroupsDialog
          open={groupsDialogOpen}
          onOpenChange={setGroupsDialogOpen}
          participants={allParticipants}
          initialGroups={draftGroups}
          onSave={handleSaveGroups}
          saving={savingGroups}
        />

        <ParticipantDetailDialog
          participant={activeParticipant}
          open={!!activeParticipant}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              closeParticipantDialog();
            }
          }}
          onOpenGroupsManager={() => setGroupsDialogOpen(true)}
          guestForm={
            activeParticipant
              ? guestForms[activeParticipant.userId.toString()] ?? activeParticipant.moderatorNotes ?? EMPTY_GUEST_FORM
              : EMPTY_GUEST_FORM
          }
          onGuestFormChange={handleGuestFormChange}
          onSaveGuestForm={handleSaveGuestForm}
          savingGuestForm={savingGuestForm}
          guestFormStatus={guestFormStatus}
        />
      </div>
    </div>
  );
}
